import * as THREE from 'three';
import type { Enemy } from '../ai/enemy';
import { armHitstop } from './damage';
import type { SuperMeter } from './super';
import type { PickupPool } from '../loot/pickup';
import { rollDrops } from '../loot/droptable';
import { rollEnemyExtras, rollBossExtras, type BossLootKey } from '../economy/drops';
import type { WearableId } from '../economy/wardrobe';

const KILL_HITSTOP_MS = 55;

/**
 * Wave L-1 (PLAN-CITY2 §6): both kill choke points below take these two
 * sets — `ownedWearables` (economy/drops.ts's anti-farm cap: an already-
 * owned cosmetic never rolls again) and `everKilledFirstTime` (drives the
 * rat_king-crown/golden_pigeon-plume GUARANTEED first-kill grant).
 * Deliberately plain `Set`s, not `SaveData` — this file stays decoupled from
 * the save schema exactly like `economy/drops.ts` itself; main.ts builds a
 * FRESH pair of sets (from `save.wardrobe.owned`/`save.progress.everKilled`)
 * for each combat-resolution call, one per real gameplay moment (a single
 * shot's impact, one melee swing, one super blast).
 *
 * `everKilledFirstTime` stays read-only — main.ts's own `markKilledOnce` is
 * the sole writer, called once per kill after this function returns, since
 * "has this kind EVER been killed" is a genuinely persistent fact.
 *
 * `ownedWearables` is `Set`, not `ReadonlySet`, and IS mutated by this file:
 * review fix (HIGH, correctness) — a single melee swing/super blast/multi-
 * bullet tick can kill several enemies in one synchronous batch, all sharing
 * the SAME `KillLootContext` (main.ts calls `buildLootContext()` once per
 * batch, not once per kill). Without marking a just-rolled cosmetic as
 * claimed immediately, two enemies in the same batch could independently
 * roll the SAME not-yet-owned cosmetic (the real save write only happens
 * later, at actual pickup COLLECTION) — spawning two duplicate pickups, the
 * second of which silently no-ops on collection. Adding to this Set the
 * instant a cosmetic is rolled closes that window for the rest of THIS
 * batch; it never touches `save.wardrobe.owned` itself (main.ts's own
 * `buildLootContext()` rebuilds a fresh Set from the real save on the VERY
 * NEXT call, so this mutation can't leak into a later, unrelated batch).
 *
 * `grantWearableDirect` — Wave I (PLAN-CITY3 §5, L6 "rarity claims slots
 * first"): the choke point for a GUARANTEED first-kill cosmetic. Called
 * instead of `pickups.spawn('cosmetic',...)` whenever `RolledExtras.
 * cosmeticGuaranteed` is true, so the crown/plume is written to the save the
 * instant it's earned — never at the mercy of a full 64-slot pickup pool or
 * a drop that lands somewhere unreachable. main.ts's own implementation
 * still plays the full toast/sfx/burst "celebration" at `origin` — the
 * grant is immediate, the FEEL of a pickup moment is not lost, per L6's own
 * "(pickup as celebration only)" phrasing.
 */
export interface KillLootContext {
  ownedWearables: Set<WearableId>;
  everKilledFirstTime: ReadonlySet<string>;
  grantWearableDirect: (id: WearableId, origin: THREE.Vector3) => void;
}

/**
 * Wave I (L6): spawns a roll's parts + cosmetic — parts always go through
 * the pool (they never carry a guarantee), the cosmetic branches on
 * `cosmeticGuaranteed` (see `KillLootContext`'s own comment for why). Same
 * "one pickup per unit" convention `loot/droptable.ts`'s own `rollDrops`
 * already uses for multi-coin rolls, kept consistent rather than inventing a
 * per-pickup `amount` field `loot/collect.ts` would then also need to
 * understand.
 */
function spawnRolledExtras(pickups: PickupPool, loot: KillLootContext, origin: THREE.Vector3, extras: ReturnType<typeof rollEnemyExtras>): void {
  for (let i = 0; i < extras.parts; i++) pickups.spawn('part', origin, { rarity: extras.partsRarity ?? undefined });
  if (extras.cosmetic) {
    loot.ownedWearables.add(extras.cosmetic); // claim it for the rest of THIS batch — see KillLootContext's own comment
    if (extras.cosmeticGuaranteed) loot.grantWearableDirect(extras.cosmetic, origin);
    else pickups.spawn('cosmetic', origin, { wearableId: extras.cosmetic });
  }
}

/**
 * THE single kill choke point (PLAN-CITY §0.7 / Classic gotcha #14) — every
 * damage path that can end an enemy's life (player bullets, pellets, melee,
 * the super blast) calls this exactly once, at the moment `Enemy.takeDamage`
 * returns `true`, instead of each caller re-deriving its own "and now spawn
 * loot / arm hitstop / grant super" tail. Hitstop and loot are UNCONDITIONAL
 * (a kill is a kill for feel/economy purposes, matching Classic's own
 * `killEnemy` covering every damage source uniformly) — `superMeter` is the
 * one deliberately conditional piece: pass it for a bullet/pellet/melee kill,
 * omit it for a super-blast kill, so consuming the whole meter for one blast
 * doesn't partially refund itself from its own kills (the existing S02/S03
 * behavior, preserved here rather than silently changed).
 *
 * Wave L-1: `loot/droptable.ts`'s existing coin/material/heart roll is
 * UNCHANGED (still called exactly as before); `economy/drops.ts`'s new
 * UNCOMMON->LEGENDARY layer is rolled ON TOP of it via `loot.ownedWearables`
 * (this kind's own EnemyKind entry into the shared `KillLootContext`).
 *
 * Wave I (L6): spawn ORDER changed from Wave L-1's original "commons first,
 * extras last" to extras-FIRST, commons-last — `PickupPool` has a fixed
 * 64-slot cap, and CITY3-FINDINGS.md flagged a real bug: a boss's own
 * cosmetic used to spawn dead last into a pool a sustained fight could
 * already have exhausted with ordinary coins, silently dropping the reward
 * while `markKilledOnce` still burned the one-shot "ever killed" flag either
 * way. A GUARANTEED cosmetic skips the pool entirely (`grantWearableDirect`,
 * see `KillLootContext`'s own comment); a non-guaranteed roll still spawns a
 * real pickup, just ahead of the coins so it claims a slot (and can evict
 * one, see `PickupPool.spawn`'s own eviction logic) before the pool fills.
 */
export function resolveEnemyKill(enemy: Enemy, pickups: PickupPool, loot: KillLootContext, superMeter?: SuperMeter): void {
  armHitstop(KILL_HITSTOP_MS);
  superMeter?.onKill();
  const dropOrigin = enemy.position.clone() as THREE.Vector3;
  const isFirstKill = !loot.everKilledFirstTime.has(enemy.archetype.kind);
  const extras = rollEnemyExtras(enemy.archetype.kind, loot.ownedWearables, isFirstKill);
  spawnRolledExtras(pickups, loot, dropOrigin, extras);
  for (const drop of rollDrops(enemy.archetype.kind)) {
    pickups.spawn(drop.kind, dropOrigin);
  }
}

const BOSS_KILL_HITSTOP_MS = 140; // a longer flinch than a roster kill's 55ms — a boss defeat should read as heavier, same "scale the feedback to the moment" idea Classic's own boss-clear celebrations use

/**
 * S06's boss-kill choke point — deliberately NOT `resolveEnemyKill` itself
 * (that function's signature is `Enemy`-shaped: `enemy.archetype.kind` for
 * the drop-table lookup, `enemy.position` for drop placement — `Boss` isn't
 * an `Enemy`, see ai/boss.ts's header comment). Still routes through the
 * SAME `armHitstop`/`SuperMeter.onKill` primitives every other kill path
 * uses, keeping §0.7's "one choke point" spirit even though a boss's own
 * shape needs a separate function.
 *
 * Wave L-1: a boss kill previously granted NO loot at all (flagged in this
 * function's own prior comment as "Wave C/Part 2 territory" — this IS that
 * territory now). `bossKey`/`origin` let a future den boss (`rat_king`, once
 * Wave D/M's swap-over spawns `RatKingBoss` live) call this exact same
 * function — see `economy/drops.ts`'s `BossLootKey`/`BOSS_COSMETIC_POOL` for
 * why `rat_king`'s own boss-form cosmetic table is deliberately empty today.
 */
export function resolveBossKill(bossKey: BossLootKey, origin: THREE.Vector3, pickups: PickupPool, loot: KillLootContext, superMeter?: SuperMeter): void {
  armHitstop(BOSS_KILL_HITSTOP_MS);
  superMeter?.onKill();
  const isFirstKill = !loot.everKilledFirstTime.has(bossKey);
  const extras = rollBossExtras(bossKey, loot.ownedWearables, isFirstKill);
  spawnRolledExtras(pickups, loot, origin, extras); // Wave I (L6): extras before coins, same ordering rationale as resolveEnemyKill above
  for (let i = 0; i < extras.coins; i++) pickups.spawn('coin', origin);
}
